import React, { useEffect, useState } from 'react';
import { Form, Input, Row, Col, Select, Checkbox, Button, Divider, Result, Spin, App } from 'antd';
import { useParams } from 'react-router-dom';
import onboardingService from '../../Services/onboardingService';
import ContactFields from '../../Components/onboarding/ContactFields';
import { OnboardingInvitationStatus } from '../../Constants/enums';
import { toApiError } from '../../api/axiosClient';

const TERMS_VERSION = '2026-08';

const slugify = (value = '') =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const CenteredCard = ({ children, wide }) => (
  <div className="public-viewport">
    <div className="public-card" style={wide ? { maxWidth: 860 } : undefined}>{children}</div>
  </div>
);

const OnboardingForm = () => {
  const { token } = useParams();
  const { message } = App.useApp();
  const [form] = Form.useForm();

  // loading | form | invalid | expired | alreadyUsed | submitted | error
  const [phase, setPhase] = useState('loading');
  const [orgName, setOrgName] = useState('');
  const [industries, setIndustries] = useState([]);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submittable, setSubmittable] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Live validity so the button can be disabled until everything required is valid.
  const values = Form.useWatch([], form);
  useEffect(() => {
    if (phase !== 'form') return;
    form
      .validateFields({ validateOnly: true })
      .then(() => setSubmittable(true))
      .catch(() => setSubmittable(false));
  }, [form, values, phase]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const info = await onboardingService.getForm(token);
        if (!active) return;
        if (info?.status === OnboardingInvitationStatus.Submitted) return setPhase('alreadyUsed');
        if (info?.status === OnboardingInvitationStatus.Expired) return setPhase('expired');
        if (info?.status === OnboardingInvitationStatus.Cancelled) return setPhase('invalid');
        setOrgName(info?.organizationName || '');
        form.setFieldsValue({ organizationName: info?.organizationName || '' });
        try {
          const list = await onboardingService.getIndustries();
          if (active) setIndustries(list || []);
        } catch {
          /* industries are non-fatal */
        }
        setPhase('form');
      } catch (err) {
        if (!active) return;
        const { status } = toApiError(err);
        setPhase(status === 404 ? 'invalid' : 'error');
      }
    })();
    return () => {
      active = false;
    };
  }, [token, form]);

  const onFinish = async (formValues) => {
    setSubmitting(true);
    try {
      await onboardingService.submitForm(token, {
        ...formValues,
        slug: slugify(formValues.organizationName),
        termsAndConditionsAccepted: true,
        termsAndConditionsVersion: TERMS_VERSION,
      });
      setPhase('submitted');
    } catch (err) {
      const { status, message: msg } = toApiError(err);
      if (status === 409) {
        setPhase('alreadyUsed');
      } else {
        message.error(msg || 'Could not submit the form. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (phase === 'loading') {
    return (
      <CenteredCard>
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin size="large" />
        </div>
      </CenteredCard>
    );
  }

  const RESULTS = {
    invalid: {
      status: 'warning',
      title: 'This onboarding link is not valid',
      subTitle: 'The link could not be found. Please contact your VelorHR representative for a new invitation.',
    },
    expired: {
      status: 'warning',
      title: 'This onboarding link has expired',
      subTitle: 'For security, invitations expire after a set time. Please ask your VelorHR representative to resend it.',
    },
    alreadyUsed: {
      status: 'info',
      title: 'This link has already been used',
      subTitle: 'Your onboarding information was already submitted. There is nothing more to do here.',
    },
    error: {
      status: 'error',
      title: 'Something went wrong',
      subTitle: "We couldn't load the onboarding form. Please refresh and try again.",
    },
    submitted: {
      status: 'success',
      title: 'Thank you - your information was received',
      subTitle: 'Your VelorHR onboarding details have been submitted. Our team will be in touch about the next steps. You can close this page.',
    },
  };

  if (RESULTS[phase]) {
    return (
      <CenteredCard>
        <Result {...RESULTS[phase]} />
      </CenteredCard>
    );
  }

  return (
    <CenteredCard wide>
      <div style={{ marginBottom: 8 }}>
        <div className="auth-brand" style={{ fontSize: 20 }}>Welcome to VelorHR</div>
        <div style={{ color: '#64748b' }}>
          {orgName ? `Complete onboarding for ${orgName}` : 'Complete your organization onboarding'}
        </div>
      </div>
      <Divider style={{ borderColor: '#eaecf0' }} />

      <Form form={form} layout="vertical" requiredMark="optional" onFinish={onFinish}>
        <Divider orientation="left" orientationMargin={0} style={{ borderColor: '#eaecf0' }}>Organization</Divider>
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item name="organizationName" label="Organization name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="industryId" label="Industry" rules={[{ required: true }]}>
              <Select
                placeholder="Select an industry"
                options={industries.map((i) => ({ value: i.id, label: i.name }))}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item name="physicalAddress" label="Physical address" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item name="city" label="City" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item name="state" label="State / Province" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item name="zipCode" label="Zip / Postal code" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item name="country" label="Country" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <ContactFields
          namePrefix="organizationAdmin"
          title="Organization Admin"
          description="The HR/Admin who will run VelorHR for your organization and set the account password."
        />
        <ContactFields
          namePrefix="contractAdmin"
          title="Contract Admin"
          description="Commercial and contract contact (not a VelorHR login)."
        />
        <ContactFields
          namePrefix="billingContact"
          title="Billing Contact"
          description="Receives invoices and payment communication (not a VelorHR login)."
        />

        <Divider style={{ borderColor: '#eaecf0' }} />
        <Form.Item>
          <Checkbox checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)}>
            I accept the VelorHR terms and conditions.
          </Checkbox>
        </Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          size="large"
          loading={submitting}
          disabled={!termsAccepted || !submittable}
        >
          Submit onboarding information
        </Button>
      </Form>
    </CenteredCard>
  );
};

export default OnboardingForm;
